import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import QRCode from 'qrcode';
import { Printer, QrCode, X } from 'lucide-react';
import { codeUrl } from '@/lib/desk';

/** A member's QR code as an image. Drawn on the phone, so it shows without signal once the page has loaded. */
export function QrImage({ code, size = 240, className = '' }: { code: string; size?: number; className?: string }) {
  const [src, setSrc] = useState('');
  useEffect(() => {
    let live = true;
    QRCode.toDataURL(codeUrl(code), { margin: 1, width: size * 2, errorCorrectionLevel: 'M', color: { dark: '#1C1A17', light: '#FFFFFF' } })
      .then(u => { if (live) setSrc(u); }).catch(() => setSrc(''));
    return () => { live = false; };
  }, [code, size]);
  return src ? <img src={src} width={size} height={size} alt="Member QR code" className={className} /> : <span style={{ width: size, height: size }} className={`inline-block bg-stone-100 ${className}`} />;
}

/** "Show code" and "Print" for one person, as on My household and their own page. */
export function MemberCodeButtons({ person }: { person: { id: string; first_name: string; last_name: string; member_code: string; member_number?: number | null } }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const close = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', close);
    return () => document.removeEventListener('keydown', close);
  }, [open]);
  return (
    <>
      <span className="flex flex-wrap gap-1.5">
        <button type="button" className="btn-small" onClick={() => setOpen(true)}><QrCode size={13} /> Show code</button>
        <Link to={`/cards?ids=${person.id}`} className="btn-small"><Printer size={13} /> Print</Link>
      </span>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/70 p-4" role="dialog" aria-modal="true" aria-label={`${person.first_name}'s code`} onClick={() => setOpen(false)}>
          <div className="w-full max-w-sm space-y-3 rounded-sm bg-white p-6 text-center" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div className="text-left">
                <p className="text-xl font-bold">{person.first_name} {person.last_name}</p>
                {person.member_number != null && <p className="text-sm text-ink-500">EGKA member no. {person.member_number}</p>}
              </div>
              <button type="button" className="btn-ghost px-2" aria-label="Close" onClick={() => setOpen(false)}><X size={18} /></button>
            </div>
            <QrImage code={person.member_code} size={280} className="mx-auto" />
            <p className="text-sm text-ink-500">Show this at the check-in desk at EGKA events. Turn your screen brightness up if it does not scan.</p>
          </div>
        </div>
      )}
    </>
  );
}
